import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Container, Row } from "react-bootstrap";
import { useMutation } from "@apollo/client";

import BaseLayout from "../../components/layout";
import Header from "../../components/layout/headers/Header";
import DepositModal from "./components/DepositModal";
import { DEPOSIT } from "../../graphql/mutations/transaction";
import DepositTable from "./components/DepositTable";
import {
  showErrorToast,
  showSuccessToast,
} from "../../components/common/CustomErrorMessage";
import {
  useDepositsQuery,
  useQueryDeposits,
} from "../../graphql/apollo/query-hooks";

const Deposit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showFirstModal, setShowFirstModal] = useState(false);
  const [data, setData] = useState({
    profit: 0,
    amount: 0,
    min: 0,
    max: undefined,
    duration: 0,
  });

  const toggleFirstModal = () => setShowFirstModal(!showFirstModal);

  const openModal = (profit, min, max, duration) => {
    toggleFirstModal();
    setData({ ...data, profit, min, max, duration });
  };

  const { memberDeposits, refetch } = useDepositsQuery();
  const { deposits } = useQueryDeposits();
  const [deposit, { loading }] = useMutation(DEPOSIT, {
    variables: {
      input: {
        profit: data.profit,
        amount: data.amount,
        duration: data.duration,
      },
    },
    onCompleted: ({ deposit }) => {
      if (deposit) {
        showSuccessToast();
        setData({ profit: 0, amount: 0, min: 0, max: undefined, duration: 0 });
        toggleFirstModal();
        refetch();
      }
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleChange = (e) => {
    setData({ ...data, amount: parseInt(e.target.value) });
  };

  const handleConfirm = async () => {
    if (data.amount < data.min) {
      showErrorToast(`${t("minAmount")} ${data.min}`);
      return;
    } else if (data.max && data.amount > data.max) {
      showErrorToast(`${t("maxAmount")} ${data.max}`);
      return;
    }
    await deposit();
  };

  return (
    <BaseLayout>
      <DepositModal
        show={showFirstModal}
        handleClose={toggleFirstModal}
        handleChange={handleChange}
        handleConfirm={handleConfirm}
        loading={loading}
      />
      <Header title={t("deposit")} onGoback={() => navigate(-1)} />
      <Container className="mt-2">
        {deposits?.short?.enabled && (
          <Card bg="secondary" className="p-2">
            <h5 className="text-white text-center">
              {deposits.short?.duration} วัน Financial Management
            </h5>
            <Row className="mt-3 mb-2">
              <Col xs="6">
                <h6 className="text-center text-white">{t("amount")}</h6>
                <h6 className="text-center text-white">
                  {deposits.short?.min}~{deposits.short?.max}
                </h6>
              </Col>
              <Col xs="6">
                <h6 className="text-center text-white">รายได้</h6>
                <h6 className="text-center text-white">
                  {(deposits?.short?.percent * 100).toFixed(2)}%
                </h6>
              </Col>
            </Row>
            <Button
              onClick={() =>
                openModal(
                  deposits?.short?.percent,
                  deposits.short?.min,
                  deposits.short?.max,
                  deposits?.short?.duration,
                )
              }
              variant="info"
            >
              ฝากตอนนี้
            </Button>
          </Card>
        )}
        {deposits?.medium?.enabled && (
          <Card bg="secondary" className="p-2 mt-3">
            <h5 className="text-white text-center">
              {deposits?.medium?.duration} วัน Financial Management
            </h5>
            <Row className="mt-3 mb-2">
              <Col xs="6">
                <h6 className="text-center text-white">{t("amount")}</h6>
                <h6 className="text-center text-white">
                  {deposits.medium?.min}~{deposits.medium?.max}
                </h6>
              </Col>
              <Col xs="6">
                <h6 className="text-center text-white">รายได้</h6>
                <h6 className="text-center text-white">
                  {(deposits?.medium?.percent * 100).toFixed(2)}%
                </h6>
              </Col>
            </Row>
            <Button
              onClick={() =>
                openModal(
                  deposits?.medium?.percent,
                  deposits.medium?.min,
                  deposits.medium?.max,
                  deposits?.medium?.duration,
                )
              }
              variant="info"
            >
              ฝากตอนนี้
            </Button>
          </Card>
        )}
        {deposits?.long?.enabled && (
          <Card bg="secondary" className="p-2 mt-3">
            <h5 className="text-white text-center">
              {deposits?.long?.duration} วัน Financial Management
            </h5>
            <Row className="mt-3 mb-2">
              <Col xs="6">
                <h6 className="text-center text-white">{t("amount")}</h6>
                <h6 className="text-center text-white">
                  {deposits.long?.min}~{deposits.long?.max}+
                </h6>
              </Col>
              <Col xs="6">
                <h6 className="text-center text-white">รายได้</h6>
                <h6 className="text-center text-white">
                  {(deposits?.long?.percent * 100).toFixed(2)}%
                </h6>
              </Col>
            </Row>
            <Button
              onClick={() =>
                openModal(
                  deposits?.long?.percent,
                  deposits.long?.min,
                  deposits.long?.max,
                  deposits?.long?.duration,
                )
              }
              variant="info"
            >
              ฝากตอนนี้
            </Button>
          </Card>
        )}
        <Card className="mt-3">
          <Card.Body>
            <h6>{t("depositList")}</h6>
            <hr />
            <DepositTable data={memberDeposits} />
          </Card.Body>
        </Card>
      </Container>
    </BaseLayout>
  );
};

export default Deposit;
